import { getCompany } from 'helpers/getCompanyVariables';

const company = getCompany();

export const SUNCARE_CARRIER_NAME = 'Suncare';

export const SUNCARE_ZOHO_FIELDS = {
  ContractSent: 'Contract_Sent',
  SignedContract: 'Signed_Contract',
  ValidCertificate: 'Valid_Certificate'
};

export const SUNCARE_CONTRACT_URLS = {
  Independent: 'https://sign.zoho.com/zsfl/hrkC6E0BLgK4QOzrDPky?i=8079',
  GA: 'https://sign.zoho.com/zsfl/vpfPcmJ01932ZqZIBXIM?i=7512',
  MGA: 'https://sign.zoho.com/zsfl/TIBljXH8j2ov1w9MIoFy?i=6501'
};

export const SUNCARE_PHASES = {
  signContract: 'signContract',
  pendingDirector: 'pendingDirector',
  uploadCertificate: 'uploadCertificate',
  complete: 'complete'
};

export const SUNCARE_REQUIRED_FIELDS = {
  suncareHasAgency: null,
  suncareAgentCount: '',
  suncareProjectedPolicies: '',
  suncareContractOpened: false
};

// Derived from SUNCARE_REQUIRED_FIELDS so adding a new field can never be forgotten here.
export const SUNCARE_TRANSIENT_FIELDS = Object.keys(SUNCARE_REQUIRED_FIELDS);

function normalizeSuncareCarrierName(carrierName) {
  return carrierName?.trim().toLowerCase().replace(/\s+/g, '') ?? '';
}

export function isSuncareContext(carrierName) {
  return (
    company === 'sunshine' &&
    normalizeSuncareCarrierName(carrierName) ===
      normalizeSuncareCarrierName(SUNCARE_CARRIER_NAME)
  );
}

export function isSuncareAppointmentBlocked(user, carrierName) {
  if (!isSuncareContext(carrierName) || !user) return false;

  const phase = getSuncarePhase(user);
  return (
    phase === SUNCARE_PHASES.pendingDirector ||
    phase === SUNCARE_PHASES.uploadCertificate
  );
}

export function getSuncareCarrierCardStatus(user, carrierName) {
  if (!isSuncareContext(carrierName) || !user) return null;
  return getSuncareCardStatus(getSuncarePhase(user));
}

/**
 * Single source of truth for all Suncare UI flags in the sidebar/side section.
 * Kept as one call to avoid the duplicated derivation that used to live in
 * SideSection.jsx and SideSectionUnnapointed.jsx.
 */
export function getSuncareUiState(user, carrierName) {
  const isCarrier = isSuncareContext(carrierName);
  const phase = isCarrier && user ? getSuncarePhase(user) : null;
  const isPendingDirector = phase === SUNCARE_PHASES.pendingDirector;
  const isTrainingPending = phase === SUNCARE_PHASES.uploadCertificate;
  return {
    isCarrier,
    phase,
    isPendingDirector,
    isTrainingPending,
    isAppointmentBlocked: isPendingDirector || isTrainingPending
  };
}

export function getSuncarePhase(user) {
  if (!user) return null;

  const contractSent = user[SUNCARE_ZOHO_FIELDS.ContractSent];
  const signedContract = user[SUNCARE_ZOHO_FIELDS.SignedContract];
  const validCertificate = user[SUNCARE_ZOHO_FIELDS.ValidCertificate];

  if (validCertificate) return SUNCARE_PHASES.complete;
  if (contractSent && signedContract) return SUNCARE_PHASES.uploadCertificate;
  if (contractSent) return SUNCARE_PHASES.pendingDirector;
  return SUNCARE_PHASES.signContract;
}

function parseNonNegativeInteger(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
    return null;
  }
  return parsed;
}

export function getSuncareQuestionnaireAnswers(formData) {
  return {
    hasAgency: formData?.suncareHasAgency,
    agentCount: formData?.suncareAgentCount,
    projectedPolicies: formData?.suncareProjectedPolicies
  };
}

export function isSuncareContractTypeDeterminate(answers) {
  const { hasAgency, agentCount, projectedPolicies } = answers ?? {};

  if (hasAgency === null || hasAgency === undefined) return false;
  if (hasAgency === false) return true;

  const parsedAgentCount = parseNonNegativeInteger(agentCount);
  if (parsedAgentCount === null) return false;
  if (parsedAgentCount < 5) return true;

  return parseNonNegativeInteger(projectedPolicies) !== null;
}

export function getSuncareContractType(answers) {
  const { hasAgency, agentCount, projectedPolicies } = answers ?? {};

  if (hasAgency === null || hasAgency === undefined) return null;
  if (!hasAgency) return 'Independent';

  const parsedAgentCount = parseNonNegativeInteger(agentCount);
  if (parsedAgentCount === null) return null;
  if (parsedAgentCount < 5) return 'Independent';

  const parsedPolicies = parseNonNegativeInteger(projectedPolicies);
  if (parsedPolicies === null) return null;
  if (parsedPolicies < 50) return 'Independent';
  if (parsedPolicies < 100) return 'GA';
  return 'MGA';
}

export function getSuncareContractUrl(contractType) {
  return SUNCARE_CONTRACT_URLS[contractType] ?? null;
}

export function getSuncareCardStatus(phase) {
  if (phase === SUNCARE_PHASES.pendingDirector) return 'In progress';
  if (phase === SUNCARE_PHASES.uploadCertificate) {
    return company === 'sunshine' ? 'Pending Agent Action' : 'Action Needed';
  }
  return null;
}

export function getSuncareTrainingBadgeClassName() {
  return company === 'sunshine'
    ? '[&>span]:!bg-blue [&>span]:!text-white'
    : '[&>span]:!bg-semantic-danger [&>span]:!text-white';
}

export function buildSuncareRequiredActions(phase) {
  if (phase === SUNCARE_PHASES.signContract) {
    return [
      {
        title: 'Suncare Contract Signing',
        description:
          'Complete the questionnaire below, then sign your contract in Zoho Sign.',
        accept: null,
        expires: false,
        required: true,
        state: null,
        name: 'Suncare_Contract',
        type: 'suncareContractSigning',
        carrierBased: false,
        defaultExpirationDate: null,
        suncareBypassFilter: true
      }
    ];
  }

  if (phase === SUNCARE_PHASES.uploadCertificate) {
    return [
      {
        title: 'Suncare Training Accounts',
        description:
          'LMS training instructions were sent to your email with the subject "Suncare - Training Accounts". Complete the training and upload your certificate using the link in that email.',
        accept: null,
        expires: false,
        required: false,
        state: null,
        name: 'Suncare_Training_Notice',
        type: 'suncareTrainingNotice',
        carrierBased: false,
        defaultExpirationDate: null,
        suncareBypassFilter: true
      }
    ];
  }

  return [];
}
